import { useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { Box, Text, useDangerousConfirmationDialog } from '@factorialco/deprecated-design-system'
import { AccountPageDocument, DeleteBankingBankAccountDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AccountDetailSidepanel } from 'modules/banking/components/AccountDetailSidepanel'
import { useUpsertLedgerBankAccountResource } from 'modules/banking/components/AccountDetailSidepanel/SidepanelAccountingInfo/useUpsertLedgerBankAccountResource'
import { AccountTransactionsImportModal } from 'modules/banking/components/AccountTransactionsImportModal'
import AddBankAccountMembershipModal from 'modules/banking/components/AddBankAccountMembershipModal'
import AddFundsModal from 'modules/banking/components/AddFundsModal'
import { PendingCardPayments } from 'modules/banking/components/transactions/PendingCardPayments'
import { AccountActionsContext } from 'modules/banking/contexts/AccountActionsContext'
import { ImportTransactionsContext } from 'modules/banking/contexts/ImportTransactionsContext'
import { ProviderConfigurationContext } from 'modules/banking/contexts/ProviderConfigurationContext'
import { accountNumberExtraFieldLabel } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

const AccountPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id: bankAccountId } = useParams()
  const [isOpen, toggleOpen] = useState(false)
  const [showFundModal, setShowFundModal] = useState<boolean>(false)
  const [showExportSidepanel, setShowExportSidepanel] = useState<boolean>(false)
  const [showImportModal, setShowImportModal] = useState<boolean>(false)
  const [pendingBalanceModalOpen, togglePendingBalanceModalOpen] = useState(false)
  const [newNumTransactions, setNewNumTransactions] = useState<number | null>(null)
  const [ledgerAccountId, setLedgerAccountId] = useState<number | undefined>(undefined)
  const { openDialog } = useDangerousConfirmationDialog()
  const { upsertAccountingBankAccountResource, loading: savingLedgerAccount } =
    useUpsertLedgerBankAccountResource()
  const [showAddBankAccountMembershipModal, setShowAddBankAccountMembershipModal] =
    useState<boolean>(false)

  const { data, loading, error } = useQuery(AccountPageDocument, {
    variables: {
      id: Number(bankAccountId),
    },
  })

  const [deleteBankAccount] = useMutation(DeleteBankingBankAccountDocument, {
    extractorFn: (dataDeleted) => dataDeleted.bankingMutations.deleteBankingBankAccount,
  })

  const {
    reconciliationsSeeFinancialDocumentsPage,
    reconciliationsSeeJournalEntriesPage,
    bankAccountsSeeDetailPage,
  } = data?.banking?._policies || {
    reconciliationsSeeFinancialDocumentsPage: { can: false },
    reconciliationsSeeJournalEntriesPage: { can: false },
    bankAccountsSeeDetailPage: { can: false },
  }

  const canManageAccountResources =
    data?.finance._policies.accountingManageAccountResources?.can ?? false

  const bankAccount = data?.banking?.bankAccount
  const account = bankAccount?.account
  const configuration = account?.providerConfiguration
  const canReconcile =
    bankAccount &&
    (reconciliationsSeeFinancialDocumentsPage.can || reconciliationsSeeJournalEntriesPage.can)
  const canImportTransactions = canReconcile && configuration?.services?.includes('import_data')
  const selectedLegalEntityName = account?.legalEntity.legalName || ''
  const selectedLegalEntityId = account?.legalEntity.id
  const onboarding = account?.onboarding
  const onboardingStripeUrl = configuration?.services?.includes('onboarding')
    ? onboarding?.onboardingUrl
    : null

  const onDeleteConfirmation = async () => {
    if (!bankAccount) return
    track(trackingEvents.accounting.banking.deleteBankAccount)
    await deleteBankAccount({
      variables: {
        id: bankAccount.id,
      },
    })
    app.setFlash({
      type: 'correct',
      message: i18n.t('banking.accounts.delete.result'),
    })
    navigate(navigation.finance.banking.accounts.fullPath())
  }

  const linkAccount = async (newAccountId?: number) => {
    if (!bankAccount || !selectedLegalEntityId) return

    const result = await upsertAccountingBankAccountResource({
      id: bankAccount.accountResource?.id,
      legalEntityId: selectedLegalEntityId,
      accountId: newAccountId,
      resourceId: bankAccount.id,
      resourceType: 'bankaccount',
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    }
  }

  const reconcileCTA = canReconcile
    ? () =>
        navigate(
          navigation.finance.banking.accounts.accountId.reconciliations.fullPath({
            id: bankAccount.id,
          })
        )
    : undefined
  const importTransactionsCTA = canImportTransactions
    ? () => {
        setShowImportModal(true)
      }
    : undefined
  const exportTransactionsCTA = () => {
    setShowExportSidepanel(true)
  }

  return (
    <ProviderConfigurationContext.Provider value={{ configuration }}>
      <ImportTransactionsContext.Provider value={{ newNumTransactions, setNewNumTransactions }}>
        <AccountActionsContext.Provider
          value={{
            reconcileCTA,
            importTransactionsCTA,
            exportTransactionsCTA,
            currency: bankAccount?.currency,
          }}
        >
          <ResponsivePage.Body
            authorized={!!bankAccount && bankAccountsSeeDetailPage.can}
            variant="full"
            loading={loading}
            error={error}
            onActionClick={(key) => {
              if (key === 'show_detail') {
                toggleOpen(true)
              } else if (key === 'show_add_funds') {
                setShowFundModal(true)
              }
            }}
          >
            {bankAccount && account && onboarding && selectedLegalEntityId && (
              <>
                <Box flexDirection="column" height="full" width="full" gap="s16">
                  <Outlet />
                  {showFundModal && (
                    <AddFundsModal
                      beneficiaryName={bankAccount.beneficiaryName ?? '-'}
                      accountNumber={bankAccount.iban ?? bankAccount.accountNumber ?? '-'}
                      bic={bankAccount.bic ?? bankAccount.sortCode ?? '-'}
                      extraFieldLabel={
                        accountNumberExtraFieldLabel(bankAccount.accountNumberType) || ''
                      }
                      mode="addFunds"
                      onClose={() => setShowFundModal(false)}
                    />
                  )}

                  {showAddBankAccountMembershipModal && (
                    <AddBankAccountMembershipModal
                      onClose={() => setShowAddBankAccountMembershipModal(false)}
                      bankAccountId={bankAccount.id}
                    />
                  )}

                  {showExportSidepanel && (
                    <ExportSidepanel
                      resource="banking.transaction"
                      overrideFilters={{ bank_account_id: [bankAccount.id] }}
                      onClose={() => setShowExportSidepanel(false)}
                    />
                  )}

                  {showImportModal && (
                    <AccountTransactionsImportModal
                      onClose={() => setShowImportModal(false)}
                      bankAccount={bankAccount}
                    />
                  )}
                </Box>
                <AccountDetailSidepanel
                  showLedgerAccountSelector={canManageAccountResources}
                  hideDeleteButton={
                    account.onboarding.provider === 'stripe' ||
                    account.onboarding.provider === 'swan'
                  }
                  onAddBankAccountMembershipClick={() => {
                    setShowAddBankAccountMembershipModal(true)
                  }}
                  onDeleteAccountClick={() => {
                    openDialog({
                      title: i18n.t('banking.accounts.delete.modal.title'),
                      description: (
                        <Box flexDirection="column" gap="s24">
                          <Text.Normal>
                            {i18n.t('banking.accounts.delete.modal.question')}
                          </Text.Normal>
                          <Text.Normal>
                            {i18n.t('banking.accounts.delete.modal.instructions', {
                              last4digits: bankAccount.accountNumber?.slice(-4),
                            })}
                          </Text.Normal>
                        </Box>
                      ),
                      inputLabel: i18n.t('banking.accounts.delete.modal.input_label'),
                      validationText: bankAccount.accountNumber?.slice(-4),
                      confirmButtonText: i18n.t('common.delete'),
                      onConfirm: onDeleteConfirmation,
                    })
                  }}
                  ledgerAccountId={ledgerAccountId}
                  onAccountingInfoChange={setLedgerAccountId}
                  onSaveLedgerAccount={() => linkAccount(ledgerAccountId)}
                  disabledSaveButton={
                    ledgerAccountId === undefined ||
                    ledgerAccountId === bankAccount.accountResource?.account.id
                  }
                  loading={savingLedgerAccount}
                  legalEntityId={selectedLegalEntityId}
                  legalEntityName={selectedLegalEntityName}
                  payoutBankAccount={account.payoutBankAccounts[0]}
                  bankAccount={bankAccount}
                  bankAddress={account.bankAddress}
                  companyName={account.companyName}
                  open={isOpen}
                  url={onboardingStripeUrl}
                  onClose={() => toggleOpen(false)}
                  onboardingAuthorEmployeeId={account.onboarding.authorEmployee?.id}
                />
                <Sidepanel
                  width="wide"
                  open={pendingBalanceModalOpen}
                  onClose={() => togglePendingBalanceModalOpen(false)}
                  title={i18n.t('banking.transactions.authorizations.pending_transaction_details')}
                >
                  <PendingCardPayments accountId={bankAccount.id} />
                </Sidepanel>
              </>
            )}
          </ResponsivePage.Body>
        </AccountActionsContext.Provider>
      </ImportTransactionsContext.Provider>
    </ProviderConfigurationContext.Provider>
  )
}

export default AccountPage
